import { AxiosResponse } from "axios";
import { axiosRequest } from "../api.utils";
import { API_ROOT } from "services/baseURLs.config";
import { CompanyLoginDto } from "model/Login";
import {
  CompanyCustomer,
  CompanyCustomerProduct,
  CompanyDto,
  CompanyOBSetting,
  CompanyRequestDto,
  CompanyUser,
  CompanyUserRequestDto,
  StampClockTemplate,
  UserRoleRequest,
} from "model/Company";
import { application } from "./application.service";
import { gig } from "./gig.service";
import { companyEmployees } from "./companyEmployees";
import { calendar } from "./calendar.service";
import { worker } from "./worker.service";
import { timeReport } from "./timeReport.service";
import { staffingWorkerPool } from "./staffingWorkerPool.service";
import { CompanyFavoriteDto } from "../../../model/Favorite";
import { TimeReportPaymentDto } from "model/TimeReport";
import { tip } from "./companyTip.service";
import { systemSettings } from "./systemSettings.service";
import { salary } from "./salary.service";
import { organizationalStructure } from "./organizationalStructure.service";
import { schedule } from "./schedule.service";

const login = async (uid: string): Promise<AxiosResponse<CompanyLoginDto>> => {
  return await axiosRequest.auth.get<CompanyLoginDto>(
    `${API_ROOT}/login/company/${uid}`
  );
};

const addOrUpdateCompany = async (
  companyData: CompanyRequestDto,
  companyId?: number
): Promise<AxiosResponse<CompanyDto>> => {
  if (companyId) {
    return await axiosRequest.auth.put<CompanyDto>(
      `${API_ROOT}/companies/${companyId}`,
      companyData
    );
  } else {
    return await axiosRequest.auth.post<CompanyDto>(
      `${API_ROOT}/companies`,
      companyData
    );
  }
};

const addCompanyFavorites = async (
  companyId: number,
  workerId: number
): Promise<AxiosResponse<CompanyFavoriteDto>> => {
  return await axiosRequest.auth.post<any>(
    `${API_ROOT}/companies/${companyId}/favorites`,
    { workerId }
  );
};

const addCompanyFavoritesByNumber = async (
  companyId: number,
  data: any
): Promise<AxiosResponse<CompanyFavoriteDto>> => {
  return await axiosRequest.auth.post<any>(
    `${API_ROOT}/companies/${companyId}/favorites`,
    data
  );
};

const updateCompanyFavorite = async (
  companyId: number,
  data: any,
  favoriteId: number
): Promise<AxiosResponse<CompanyFavoriteDto>> => {
  return await axiosRequest.auth.put<any>(
    `${API_ROOT}/companies/${companyId}/favorites/${favoriteId}`,
    data
  );
};

const removeCompanyFavorites = async (
  companyId: number,
  id: number
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.delete(
    `${API_ROOT}/companies/${companyId}/favorites/${id}`
  );
};

const getCompanyFavorites = async (
  companyId: number
): Promise<AxiosResponse<CompanyFavoriteDto[]>> => {
  return await axiosRequest.auth.get<CompanyFavoriteDto[]>(
    `${API_ROOT}/companies/${companyId}/favorites`
  );
};

const getCompanyUsers = async (
  companyId: number
): Promise<AxiosResponse<CompanyUser[]>> => {
  return await axiosRequest.auth.get<CompanyUser[]>(
    `${API_ROOT}/companies/${companyId}/users`
  );
};
const getCompanySubscriptionInfo = async (
  companyId: number
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/membership`
  );
};

const addCompanyUser = async (
  companyId: number,
  user: CompanyUserRequestDto
): Promise<AxiosResponse<CompanyUser>> => {
  return await axiosRequest.auth.post(
    `${API_ROOT}/companies/${companyId}/users`,
    user
  );
};

const updateCompanyUser = async (
  companyId: number,
  userId: number,
  firebaseId: string,
  role: UserRoleRequest,
  firstName: string,
  lastName: string
): Promise<AxiosResponse<CompanyUser>> => {
  return await axiosRequest.auth.put(
    `${API_ROOT}/companies/${companyId}/users/${userId}/role`,
    { firebaseId, role, firstName, lastName }
  );
};

const deleteCompanyUser = async (
  companyId: number,
  userId: number
): Promise<AxiosResponse<CompanyUser>> => {
  return await axiosRequest.auth.delete(
    `${API_ROOT}/companies/${companyId}/users/${userId}`
  );
};

const createPayoutForTimeReport = async (
  companyId: number,
  timeReportId: number
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.post<TimeReportPaymentDto>(
    `${API_ROOT}/companies/${companyId}/timereports/${timeReportId}/pay`,
    { timeReportId: timeReportId, companyId: companyId }
  );
};

const getCompanyInformation = async (orgNumber: string) => {
  return axiosRequest.auth.get<any>(
    `${API_ROOT}/CompanyInfo?organisationNumber=${orgNumber}`
  );
};

const setupSimflexityPayForCompany = async (
  companyId: number
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.post(
    `${API_ROOT}/company/${companyId}/SimflexityPay`
  );
};

const addCompanyObSetting = async (
  companyId: number,
  obSetting: CompanyOBSetting
): Promise<AxiosResponse<CompanyOBSetting>> => {
  return await axiosRequest.auth.post<any>(
    `${API_ROOT}/companies/${companyId}/OBSetting`,
    obSetting
  );
};

const getCompanyObSettings = async (
  companyId: number
): Promise<AxiosResponse<CompanyOBSetting[]>> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/OBSetting`
  );
};

const deleteCompanyObSettings = async (
  companyId: number,
  obSettingId: number
): Promise<AxiosResponse<CompanyOBSetting[]>> => {
  return await axiosRequest.auth.delete<any>(
    `${API_ROOT}/companies/${companyId}/OBSetting/${obSettingId}`
  );
};

const getTimeReportForStamping = async (
  companyId: number,
  workerPhoneNumber: string
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/stampClock/${workerPhoneNumber}`
  );
};

const getStampClockTemplate = async (
  companyId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/stampClock/template`
  );
};

const updateStampClockTemplate = async (
  data: StampClockTemplate
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<any>(
    `${API_ROOT}/companies/${data.companyId}/stampClock/template`,
    data
  );
};

const getLocalStampClockOnlySetting = async (
  companyId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/stampClock/settings/localOnly`
  );
};

const updateLocalStampClockOnlySetting = async (
  companyId: number,
  active: boolean
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<any>(
    `${API_ROOT}/companies/${companyId}/stampClock/settings/localOnly?active=${active}`
  );
};

const getStampClockTimeReport = async (
  companyId: number,
  workerPhoneNumber: string,
  timeReportId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/stampClock/${workerPhoneNumber}/timereports/${timeReportId}`
  );
};

const clockStampTimeReport = async (
  companyId: number,
  workerPhoneNumber: string,
  timeReportId: number,
  data: any
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<any>(
    `${API_ROOT}/companies/${companyId}/stampClock/${workerPhoneNumber}/timereports/${timeReportId}/submit`,
    data
  );
};

const getAutogenerateShiftSetting = async (
  companyId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/stampClock/settings/autoGenerate`
  );
};

const updateAutogenerateShiftSetting = async (
  companyId: number,
  active: boolean
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<any>(
    `${API_ROOT}/companies/${companyId}/stampClock/settings/autoGenerate?active=${active}`
  );
};

const getSettingForAllowingAutoGeneratedGig = async (
  companyId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/stampClock/settings/autoGenerate`
  );
};

const getStampClockWorkerWorkgroups = async (
  companyId: number,
  workerPhoneNumber: string
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/stampClock/${workerPhoneNumber}/workGroups`
  );
};

const createAutoGeneratedGig = async (
  companyId: number,
  workerPhoneNumber: string,
  data: any
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.post<any>(
    `${API_ROOT}/companies/${companyId}/stampClock/${workerPhoneNumber}/gigs`,
    data
  );
};

const handleCheckInCheckOut = async (
  companyId: number,
  timeReportId: number,
  workerPhoneNumber: string,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<any>(
    `${API_ROOT}/companies/${companyId}/stampClock/${workerPhoneNumber}/timereports/${timeReportId}`
  );
};

const deleteCompaniesGigsShift = async (
  companyId: number,
  shiftId: number,
  gigsId: string
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.delete<any>(
    `${API_ROOT}/companies/${companyId}/gigs/${gigsId}/shifts/${shiftId}`
  );
};

const getTotalTimeAndCostsMonthly = async (
  companyId: number,
  year: number,
  month: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/workers/summary/${year}-${month}`
  );
};

const getTotalsMonthlyWorker = async (
  companyId: number,
  workerId: number,
  year: number,
  month: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/summary/${year}-${month}`
  );
};

const getWorkerDetailsByWorkerId = async (
  companyId: number,
  workerId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}`
  );
};

const getLatestGigRatings = async (
  companyId: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/gigrating?amount=4&page=0&sortAcending=false`
  );
};

const sendNewOfferForWithdrawnShift = async (
  companyId: number,
  gigId: number,
  data: any
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.post<any>(
    `${API_ROOT}/companies/${companyId}/gigs/${gigId}/children`,
    data
  );
};

const getCompanySettings = async (
  companyId: number
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.get(
    `${API_ROOT}/companies/${companyId}/settings`
  );
};

const getAvailableTimeReportsForStamping = async (
  companyId: number,
  workerPhoneNumber: string
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.get(
    `${API_ROOT}/companies/${companyId}/stampClock/${workerPhoneNumber}/v2`
  );
};

const getCompanyWorker = async (
  companyId: number,
  workerId: number
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.get(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v2/workgroups`
  );
};
const getCompanyCustomers = async (
  companyId?: number
): Promise<AxiosResponse<CompanyCustomer[]>> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/customers`
  );
};
const getInvoiceInformationForPeriod = async (
  startDate: Date,
  endDate: Date,
  products: string[],
  companyId?: number
): Promise<AxiosResponse<CompanyCustomer[]>> => {
  return await axiosRequest.auth.post<any>(
    `${API_ROOT}/companies/${companyId}/customers/invoice-for-products`, { startDate, endDate, products }
  );
};
const addCompanyCustomer = async (
  companyId: number,
  customer: CompanyCustomer
): Promise<AxiosResponse<CompanyCustomer>> => {
  return await axiosRequest.auth.post<any>(
    `${API_ROOT}/companies/${companyId}/customers`,
    customer
  );
};
const addCompanyCustomerProduct = async (
  companyId: number,
  customerProduct: CompanyCustomerProduct
): Promise<AxiosResponse<CompanyCustomerProduct>> => {
  return await axiosRequest.auth.post<any>(
    `${API_ROOT}/companies/${companyId}/customers/product`,
    customerProduct
  );
};

const getCompanyWorkerV2 = async (
  companyId: number,
  workerId: number
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.get(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v2`
  );
};

export const company = {
  application,
  gig,
  calendar,
  worker,
  timeReport,
  login,
  companyEmployees,
  systemSettings,
  salary,
  addOrUpdateCompany,
  getCompanyFavorites,
  addCompanyFavorites,
  removeCompanyFavorites,
  createPayoutForTimeReport,
  getCompanyUsers,
  addCompanyUser,
  updateCompanyUser,
  deleteCompanyUser,
  getCompanyInformation,
  setupSimflexityPayForCompany,
  addCompanyObSetting,
  getCompanyObSettings,
  deleteCompanyObSettings,
  addCompanyFavoritesByNumber,
  updateCompanyFavorite,
  getTimeReportForStamping,
  handleCheckInCheckOut,
  staffingWorkerPool,
  deleteCompaniesGigsShift,
  getCompanySubscriptionInfo,
  getTotalTimeAndCostsMonthly,
  getTotalsMonthlyWorker,
  getStampClockTemplate,
  updateStampClockTemplate,
  getLocalStampClockOnlySetting,
  updateLocalStampClockOnlySetting,
  getAutogenerateShiftSetting,
  updateAutogenerateShiftSetting,
  createAutoGeneratedGig,
  getWorkerDetailsByWorkerId,
  getLatestGigRatings,
  sendNewOfferForWithdrawnShift,
  tip,
  getCompanySettings,
  organizationalStructure,
  getStampClockTimeReport,
  clockStampTimeReport,
  getSettingForAllowingAutoGeneratedGig,
  getStampClockWorkerWorkgroups,
  getAvailableTimeReportsForStamping,
  getCompanyWorker,
  getCompanyCustomers,
  addCompanyCustomer,
  addCompanyCustomerProduct,
  getInvoiceInformationForPeriod,
  getCompanyWorkerV2,
  schedule
};
